import { type ColumnDef } from "@tanstack/react-table";
import type { ApiAbout } from "../types";
import type { TFunction } from "i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CircleEllipsis, PenBox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavLink } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import DeleteButton from "@/components/delete-button";
import { aboutRepo } from "@/lib/repositories";
import { toast } from "sonner";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { queryClient } from "@/lib/query-client";

async function onDelete(id: string, t: TFunction) {
  if (!id) return;
  const [error] = await catchError(aboutRepo.delete(id));
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["abouts"] });
  toast.success(t("messages.about_delete_success"));
}

export const aboutColumns = (t: TFunction): ColumnDef<ApiAbout>[] => [
  {
    accessorKey: "title",
    header: t("modules.about.keys.title"),
    cell: ({ row }) => (
      <NavLink to={ROUTES.ABOUT_DETAILS(row.original.id)} end>
        <span className="font-medium truncate inline-block max-w-56 hover:underline">
          {row.getValue("title")}
        </span>
      </NavLink>
    ),
  },
  {
    accessorKey: "description",
    header: t("modules.about.keys.description"),
    cell: ({ row }) => {
      const body = row.getValue("description") as string;
      return (
        <span className="text-gray-500 truncate inline-block max-w-72">
          {body}
        </span>
      );
    },
  },
  {
    accessorKey: "is_active",
    header: t("keys.status"),
    cell: ({ row }) => {
      const isActive = row.getValue("is_active") as boolean;
      return (
        <Badge variant={isActive ? "success" : "destructive"}>
          {isActive ? t("keys.active") : t("keys.desactivated")}
        </Badge>
      );
    },
  },
  {
    accessorKey: "icon_name",
    header: t("modules.about.keys.type"),
    cell: ({ row }) => (
      <Badge variant="secondary">
        {t(`modules.about.types.${row.getValue("icon_name")}`)}
      </Badge>
    ),
  },
  {
    accessorKey: "created_at",
    header: t("keys.creation_date"),
    cell: ({ row }) => (
      <span className="text-gray-500">
        {formatDate(row.getValue("created_at"))}
      </span>
    ),
  },
  {
    id: "actions",
    header: t("keys.table.actions"),
    cell: ({ row }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Ouvrir le menu</span>
              <CircleEllipsis className="h-10 w-10 rotate-90 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <NavLink to={ROUTES.ABOUT_DETAILS(row.original.id)} end>
                <PenBox />
                {t("keys.table.edit")}
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <DeleteButton
                onClick={() => onDelete(row.original.id, t)}
                className="w-full justify-start"
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
